import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { ArrowLeft } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Card, CardBody, CardFooter } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { Spinner } from '@vnetwork/ui/primitives';
import { useTicket, useUpdateTicket } from '@/api/tickets';
import type { UpdateTicketRequestPayload } from '@/api/tickets';
import { ticketFormSchema, type TicketFormValues } from '../new/ticket-form-schema';
import { ClassificationSection } from '../new/sections/classification-section';
import { CustomerServiceSection } from '../new/sections/customer-service-section';
import { ContentSection } from '../new/sections/content-section';
import { AssigneesSection } from '../new/sections/assignees-section';
import { PriorityScheduleSection } from '../new/sections/priority-schedule-section';

/**
 * `/tickets/:id/edit` — full-page edit form pre-filled from `useTicket(id)`.
 *
 * Payload shape: `{ note: Partial<TicketUpdatePayload> }` — note.ticket_id is
 * required by `useUpdateTicket` to resolve the PUT /tickets/:id endpoint.
 *
 * Type group and ticket type selects are locked in edit mode (legacy behaviour).
 */
export default function TicketEditPage() {
  const { id } = useParams<{ id: string }>();
  const ticketId = Number(id);
  const navigate = useNavigate();
  const [submitError, setSubmitError] = useState('');

  const { data: ticketRes, isLoading } = useTicket(ticketId);
  const ticket = ticketRes?.result;

  const updateTicket = useUpdateTicket();

  const methods = useForm<TicketFormValues>({
    resolver: yupResolver(ticketFormSchema) as never,
    defaultValues: {
      ticket_type_id: 0,
      ticket_type_key: '',
      ticket_type_group: '',
      ticket_priority: 'low',
      ticket_status: 'opening',
      ticket_security_tag: 'none',
      ticket_meeting_type: 'online',
      ticket_meeting_channel: '',
      ticket_meeting_via: '',
      ticket_meeting_purpose: '',
      customer_id: 0,
      service_id: 0,
      ticket_product_name: '',
      ticket_request_id: 0,
      ticket_content: '',
      ticket_affected: '',
      ticket_avoid: '',
      ticket_root_cause: '',
      ticket_remedial_measures: '',
      ticket_internal_root_cause: '',
      ticket_internal_remedial_measures: '',
      ticket_internal_avoid: '',
      ticket_technical_assignees: [],
      ticket_cs_assignees: [],
      ticket_sale_assignees: [],
      ticket_requested_at: null,
      ticket_time_repply: null,
      ticket_estimated_completion_date: null,
      service_startdate: null,
      service_duedate: null,
      service_suspend_until: null,
      ticket_marjorable: false,
      ticket_id: ticketId,
      employee_id: 0,
    },
  });

  const { handleSubmit, reset, formState: { isSubmitting } } = methods;

  /* Pre-fill form from loaded ticket data */
  useEffect(() => {
    if (!ticket) return;
    reset({
      ticket_id: ticket.ticket_id,
      ticket_type_id: ticket.ticket_type_id ?? 0,
      ticket_type_key: ticket.ticket_type_key ?? '',
      ticket_type_group: (ticket.ticket_type_group as string) ?? '',
      ticket_priority: ticket.ticket_priority ?? 'low',
      ticket_status: ticket.ticket_status ?? 'opening',
      ticket_security_tag: (ticket.ticket_security_tag as string) ?? 'none',
      ticket_meeting_type: ticket.ticket_meeting_type ?? 'online',
      ticket_meeting_channel: ticket.ticket_meeting_channel ?? '',
      ticket_meeting_via: ticket.ticket_meeting_via ?? '',
      ticket_meeting_purpose: (ticket as unknown as Record<string, unknown>).ticket_meeting_purpose as string ?? '',
      customer_id: ticket.customer_id ?? 0,
      service_id: ticket.service_id ?? 0,
      ticket_product_name:
        Array.isArray(ticket.ticket_product_name)
          ? ticket.ticket_product_name[0] ?? ''
          : (ticket.ticket_product_name as unknown as string) ?? '',
      ticket_request_id: ticket.ticket_request_id ?? 0,
      ticket_content: ticket.ticket_content ?? '',
      ticket_affected: ticket.ticket_affected ?? '',
      ticket_avoid: ticket.ticket_avoid ?? '',
      ticket_root_cause: ticket.ticket_root_cause ?? '',
      ticket_remedial_measures: ticket.ticket_remedial_measures ?? '',
      ticket_internal_root_cause: ticket.ticket_internal_root_cause ?? '',
      ticket_internal_remedial_measures: ticket.ticket_internal_remedial_measures ?? '',
      ticket_internal_avoid: ticket.ticket_internal_avoid ?? '',
      ticket_technical_assignees: ticket.ticket_technical_assignees ?? [],
      ticket_cs_assignees: ticket.ticket_cs_assignees ?? [],
      ticket_sale_assignees: ticket.ticket_sale_assignees ?? [],
      ticket_requested_at: ticket.ticket_requested_at ?? null,
      ticket_time_repply: ticket.ticket_time_repply ?? null,
      ticket_estimated_completion_date: ticket.ticket_estimated_completion_date ?? null,
      service_startdate: ticket.service_startdate ?? null,
      service_duedate: ticket.service_duedate ?? null,
      service_suspend_until:
        (ticket as unknown as Record<string, unknown>).service_suspend_until as string ?? null,
      ticket_marjorable: Array.isArray(ticket.ticket_marjorable)
        ? Boolean(ticket.ticket_marjorable[0])
        : Boolean(ticket.ticket_marjorable),
      employee_id: ticket.employee_id ?? 0,
      customer_journey_id:
        (ticket as unknown as Record<string, unknown>).customer_journey_id as number | undefined,
    });
  }, [ticket, reset]);

  const onSubmit = async (data: TicketFormValues) => {
    setSubmitError('');
    try {
      const payload: UpdateTicketRequestPayload = {
        note: {
          ticket_id: data.ticket_id,
          ticket_type_id: data.ticket_type_id,
          ticket_request_id: data.ticket_request_id || undefined,
          ticket_priority: data.ticket_priority,
          ticket_status: data.ticket_status,
          ticket_security_tag: data.ticket_security_tag,
          ticket_content: data.ticket_content,
          customer_id: data.customer_id,
          service_id: Number(data.service_id) || 0,
          ticket_product_name: data.ticket_product_name || undefined,
          ticket_technical_assignees: data.ticket_technical_assignees,
          ticket_cs_assignees: data.ticket_cs_assignees,
          ticket_sale_assignees: data.ticket_sale_assignees,
          ticket_requested_at: data.ticket_requested_at ?? '',
          ticket_time_repply: data.ticket_time_repply ?? '',
          ticket_time_release: '',
          ticket_time_response: '',
          ticket_resolved_at: '',
          ticket_department: '',
          ticket_source: '',
          ticket_estimated_completion_date: data.ticket_estimated_completion_date ?? undefined,
          service_startdate: data.service_startdate ?? '',
          service_duedate: data.service_duedate ?? '',
          service_suspend_until: data.service_suspend_until ?? undefined,
          ticket_meeting_type: data.ticket_meeting_type || undefined,
          ticket_meeting_channel: data.ticket_meeting_channel || undefined,
          ticket_meeting_via: data.ticket_meeting_via || undefined,
          ticket_meeting_purpose: data.ticket_meeting_purpose || undefined,
          ticket_affected: data.ticket_affected || undefined,
          ticket_avoid: data.ticket_avoid || undefined,
          ticket_root_cause: data.ticket_root_cause || undefined,
          ticket_remedial_measures: data.ticket_remedial_measures || undefined,
          ticket_internal_root_cause: data.ticket_internal_root_cause || undefined,
          ticket_internal_remedial_measures: data.ticket_internal_remedial_measures || undefined,
          ticket_internal_avoid: data.ticket_internal_avoid || undefined,
          ticket_marjorable: data.ticket_marjorable,
          customer_journey_id: data.customer_journey_id,
        },
      };

      await updateTicket.mutateAsync(payload);
      navigate(`/tickets/${data.ticket_id}`);
    } catch (err) {
      setSubmitError(
        err instanceof Error ? err.message : 'Failed to update ticket. Please try again.',
      );
    }
  };

  const isPending = isSubmitting || updateTicket.isPending;

  if (isLoading) {
    return (
      <Container size="lg">
        <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--vn-space-8)' }}>
          <Spinner />
        </div>
      </Container>
    );
  }

  if (!ticket) {
    return (
      <Container size="lg">
        <Alert tone="danger" title="Ticket not found">Ticket #{ticketId} could not be loaded.</Alert>
      </Container>
    );
  }

  return (
    <Container size="lg">
      <PageHeader
        title={`Edit Ticket #${ticketId}`}
        description={ticket.ticket_type_title ?? 'Update ticket details'}
        actions={
          <Button
            variant="ghost"
            startIcon={<ArrowLeft size={15} />}
            onClick={() => navigate(`/tickets/${ticketId}`)}
          >
            Back to ticket
          </Button>
        }
      />

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Card>
            <CardBody style={{ display: 'grid', gap: 'var(--vn-space-6)' }}>
              {/* Type group + type locked in edit mode */}
              <ClassificationSection disableGroup />
              <CustomerServiceSection
                lockedCustomerId={ticket.customer_id}
                lockedCustomerName={ticket.customer_name}
              />
              <ContentSection />
              <AssigneesSection />
              <PriorityScheduleSection />

              {submitError && (
                <Alert tone="danger" title="Submission error">{submitError}</Alert>
              )}
            </CardBody>

            <CardFooter
              style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--vn-space-3)' }}
            >
              <Button
                variant="ghost"
                type="button"
                disabled={isPending}
                onClick={() => navigate(`/tickets/${ticketId}`)}
              >
                Cancel
              </Button>
              <Button type="submit" loading={isPending}>
                Save changes
              </Button>
            </CardFooter>
          </Card>
        </form>
      </FormProvider>
    </Container>
  );
}
